import { useState } from 'react';
import {
  FileText,
  Copy,
  Check,
  Share2,
  MapPin,
  PhoneCall,
  UserPlus,
  Receipt,
  ShoppingBag
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { formatINR } from '@/shared/utils/currency';
import { useBdmDsr } from './useBdmData';

interface BdmDsrWidgetProps {
  userId?: string;
  repName?: string;
}

const todayIso = () => new Date().toISOString().split('T')[0];
const yesterdayIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

export function BdmDsrWidget({ userId, repName }: BdmDsrWidgetProps) {
  const [selectedDate, setSelectedDate] = useState<string>(todayIso());
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'summary' | 'quotations' | 'orders' | 'visits' | 'followups'>('summary');

  const { data, isLoading, isError } = useBdmDsr(selectedDate, userId);

  const summary = data?.summary ?? {
    newLeadsCount: 0,
    followupsDoneCount: 0,
    visitsDoneCount: 0,
    quotationsCount: 0,
    quotationValue: 0,
    ordersCount: 0,
    orderValue: 0,
  };

  const handleCopyDsr = async () => {
    if (!data?.formattedDsrText) return;
    try {
      await navigator.clipboard.writeText(data.formattedDsrText);
      setCopied(true);
      toast.success('DSR report copied to clipboard!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy report to clipboard.');
    }
  };

  const handleShareWhatsApp = () => {
    if (!data?.formattedDsrText) return;
    const url = `https://wa.me/?text=${encodeURIComponent(data.formattedDsrText)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="rounded-card border border-border bg-card p-5 shadow-xs transition hover:shadow-xs">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-container text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-body-md font-semibold text-on-surface">
              {repName ? `${repName}'s Daily Sales Report (DSR)` : 'Daily Sales Report (DSR)'}
            </h3>
            <p className="text-body-sm text-on-surface-variant">
              Today's sales activity scorecard: leads, visits, quotations & bookings
            </p>
          </div>
        </div>

        {/* Date Selector & Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-border bg-surface-variant p-1">
            <button
              type="button"
              onClick={() => setSelectedDate(todayIso())}
              className={`rounded-lg px-2.5 py-1 text-body-sm font-semibold transition ${
                selectedDate === todayIso()
                  ? 'bg-primary text-on-primary shadow-xs'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setSelectedDate(yesterdayIso())}
              className={`rounded-lg px-2.5 py-1 text-body-sm font-semibold transition ${
                selectedDate === yesterdayIso()
                  ? 'bg-primary text-on-primary shadow-xs'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              Yesterday
            </button>
          </div>

          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="rounded-lg border border-border bg-card px-2.5 py-1 text-body-sm font-medium text-on-surface-variant shadow-xs focus:border-primary/40 focus:outline-none"
          />

          <button
            type="button"
            onClick={handleCopyDsr}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-body-sm font-semibold text-on-surface-variant hover:bg-surface-variant hover:text-on-surface transition active:scale-95 shadow-xs"
            title="Copy formatted DSR text"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-success-text" /> : <Copy className="h-3.5 w-3.5 text-on-surface-variant" />}
            <span>{copied ? 'Copied!' : 'Copy DSR'}</span>
          </button>

          <button
            type="button"
            onClick={handleShareWhatsApp}
            className="inline-flex items-center gap-1.5 rounded-lg border border-success/30 bg-success/10 px-3 py-1.5 text-body-sm font-semibold text-success-text hover:bg-emerald-100 transition active:scale-95 shadow-xs"
            title="Share via WhatsApp"
          >
            <Share2 className="h-3.5 w-3.5" />
            <span>Share</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-body-sm text-on-surface-subtle">Loading Daily Sales Report...</div>
      ) : isError ? (
        <div className="py-8 text-center text-body-sm text-danger-text">Could not load DSR metrics.</div>
      ) : (
        <div className="mt-4 space-y-4">
          {/* DSR 5-Pillar Scorecard Grid */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {/* 1. New Enquiries */}
            <div className="rounded-lg border border-border bg-card p-3 shadow-xs">
              <div className="flex items-center gap-2 text-on-surface-variant">
                <UserPlus className="h-4 w-4 text-info-text" />
                <span className="text-data-xs font-semibold uppercase tracking-wider">New Enquiries</span>
              </div>
              <div className="mt-2 text-xl font-semibold text-on-surface tabular-nums">
                {summary.newLeadsCount}
              </div>
              <p className="text-data-xs text-on-surface-subtle">Leads generated</p>
            </div>

            {/* 2. Follow-ups Done */}
            <div className="rounded-lg border border-border bg-card p-3 shadow-xs">
              <div className="flex items-center gap-2 text-on-surface-variant">
                <PhoneCall className="h-4 w-4 text-primary" />
                <span className="text-data-xs font-semibold uppercase tracking-wider">Follow-ups</span>
              </div>
              <div className="mt-2 text-xl font-semibold text-on-surface tabular-nums">
                {summary.followupsDoneCount}
              </div>
              <p className="text-data-xs text-on-surface-subtle">Interactions completed</p>
            </div>

            {/* 3. Field Visits */}
            <div className="rounded-lg border border-border bg-card p-3 shadow-xs">
              <div className="flex items-center gap-2 text-on-surface-variant">
                <MapPin className="h-4 w-4 text-success-text" />
                <span className="text-data-xs font-semibold uppercase tracking-wider">Client Visits</span>
              </div>
              <div className="mt-2 text-xl font-semibold text-on-surface tabular-nums">
                {summary.visitsDoneCount}
              </div>
              <p className="text-data-xs text-on-surface-subtle">Checked-in stops</p>
            </div>

            {/* 4. Quotations Sent */}
            <div className="rounded-lg border border-border bg-card p-3 shadow-xs">
              <div className="flex items-center gap-2 text-on-surface-variant">
                <Receipt className="h-4 w-4 text-warning-text" />
                <span className="text-data-xs font-semibold uppercase tracking-wider">Quotations</span>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-xl font-semibold text-on-surface tabular-nums">{summary.quotationsCount}</span>
                <span className="text-body-sm font-semibold text-warning-text tabular-nums">{formatINR(summary.quotationValue)}</span>
              </div>
              <p className="text-data-xs text-on-surface-subtle">Issued to clients</p>
            </div>

            {/* 5. Orders Booked */}
            <div className="rounded-lg border border-success/30 bg-success/10 p-3 shadow-xs">
              <div className="flex items-center gap-2 text-success-text">
                <ShoppingBag className="h-4 w-4 text-success-text" />
                <span className="text-data-xs font-semibold uppercase tracking-wider">Orders Booked</span>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-xl font-semibold text-success-text tabular-nums">{summary.ordersCount}</span>
                <span className="text-body-sm font-semibold text-success-text tabular-nums">{formatINR(summary.orderValue)}</span>
              </div>
              <p className="text-data-xs text-success-text/80">Confirmed sales deals</p>
            </div>
          </div>

          {/* Activity Breakdown Tabs */}
          <div className="pt-2">
            <div className="flex flex-wrap items-center gap-1 border-b border-border pb-2">
              <button
                type="button"
                onClick={() => setActiveTab('summary')}
                className={`rounded-lg px-3 py-1.5 text-body-sm font-semibold transition ${
                  activeTab === 'summary' ? 'bg-surface-container text-on-surface' : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                Report Text Preview
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('orders')}
                className={`rounded-lg px-3 py-1.5 text-body-sm font-semibold transition ${
                  activeTab === 'orders' ? 'bg-surface-container text-on-surface' : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                Orders ({data?.breakdowns?.orders?.length ?? 0})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('quotations')}
                className={`rounded-lg px-3 py-1.5 text-body-sm font-semibold transition ${
                  activeTab === 'quotations' ? 'bg-surface-container text-on-surface' : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                Quotations ({data?.breakdowns?.quotations?.length ?? 0})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('visits')}
                className={`rounded-lg px-3 py-1.5 text-body-sm font-semibold transition ${
                  activeTab === 'visits' ? 'bg-surface-container text-on-surface' : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                Visits ({data?.breakdowns?.visits?.length ?? 0})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('followups')}
                className={`rounded-lg px-3 py-1.5 text-body-sm font-semibold transition ${
                  activeTab === 'followups' ? 'bg-surface-container text-on-surface' : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                Follow-ups ({data?.breakdowns?.followups?.length ?? 0})
              </button>
            </div>

            {/* Tab Contents */}
            <div className="mt-3">
              {activeTab === 'summary' && (
                <div className="relative rounded-lg border border-border bg-surface-variant/60 p-4 font-mono text-body-sm text-on-surface whitespace-pre-wrap leading-relaxed shadow-inner">
                  {data?.formattedDsrText}
                </div>
              )}

              {activeTab === 'orders' && (
                <div className="space-y-2">
                  {data?.breakdowns?.orders?.length === 0 ? (
                    <p className="py-4 text-center text-body-sm text-on-surface-subtle">No orders booked on this date.</p>
                  ) : (
                    data?.breakdowns?.orders?.map((o: any) => (
                      <div key={o._id} className="flex items-center justify-between rounded-lg border border-border bg-card p-3 text-body-sm">
                        <div>
                          <span className="font-semibold text-on-surface">{o.documentNumber}</span>
                          <span className="mx-2 text-on-surface-subtle">·</span>
                          <span className="font-medium text-on-surface-variant">{o.customerName}</span>
                        </div>
                        <span className="font-semibold text-success-text tabular-nums">{formatINR(o.grandTotal)}</span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'quotations' && (
                <div className="space-y-2">
                  {data?.breakdowns?.quotations?.length === 0 ? (
                    <p className="py-4 text-center text-body-sm text-on-surface-subtle">No quotations sent on this date.</p>
                  ) : (
                    data?.breakdowns?.quotations?.map((q: any) => (
                      <div key={q._id} className="flex items-center justify-between rounded-lg border border-border bg-card p-3 text-body-sm">
                        <div>
                          <span className="font-semibold text-on-surface">{q.documentNumber}</span>
                          <span className="mx-2 text-on-surface-subtle">·</span>
                          <span className="font-medium text-on-surface-variant">{q.customerName}</span>
                        </div>
                        <span className="font-semibold text-on-surface tabular-nums">{formatINR(q.grandTotal)}</span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'visits' && (
                <div className="space-y-2">
                  {data?.breakdowns?.visits?.length === 0 ? (
                    <p className="py-4 text-center text-body-sm text-on-surface-subtle">No client visits checked-in on this date.</p>
                  ) : (
                    data?.breakdowns?.visits?.map((v: any, i: number) => (
                      <div key={i} className="flex items-center justify-between rounded-lg border border-border bg-card p-3 text-body-sm">
                        <div>
                          <span className="font-semibold text-on-surface">{v.companyName || 'Client Stop'}</span>
                          <span className="mx-2 text-on-surface-subtle">·</span>
                          <span className="text-on-surface-variant">{v.purpose || 'Meeting'}</span>
                        </div>
                        <span className="rounded-md bg-success/10 px-2 py-0.5 text-data-xs font-semibold text-success-text">Checked In</span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'followups' && (
                <div className="space-y-2">
                  {data?.breakdowns?.followups?.length === 0 ? (
                    <p className="py-4 text-center text-body-sm text-on-surface-subtle">No follow-ups recorded on this date.</p>
                  ) : (
                    data?.breakdowns?.followups?.map((f: any, i: number) => (
                      <div key={i} className="rounded-lg border border-border bg-card p-3 text-body-sm">
                        <div className="flex items-center justify-between font-semibold text-on-surface">
                          <span className="capitalize">{f.contactMethod || 'Call'} Follow-up ({f.parentType})</span>
                          <span className="text-data-xs font-normal text-on-surface-subtle">Completed</span>
                        </div>
                        <p className="mt-1 text-on-surface-variant">{f.followupNote}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
